import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { App, Select as AntSelect } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select as CustomSelect } from '../../../custom';
import { createUpdatePrivilege, getPermissions, getRoles } from '../request';

interface Props {
  privilege: Privilege;
  handleClose: () => void;
  refetch: () => void;
}

const EditPrivilege = ({ privilege, handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [rolesQuery, permissionsQuery] = useQueries({
    queries: [
      {
        queryKey: ['get-roles'],
        queryFn: getRoles,
      },
      {
        queryKey: ['get-all-permissions'],
        queryFn: getPermissions,
      },
    ],
  });

  const validate = Yup.object().shape({
    roleId: Yup.string().required('Role is required'),
    permissionIds: Yup.array().min(1, 'At least one permission is required').required('Permission is required'),
  });

  const updatePrivilegeMutation = useMutation({
    mutationFn: createUpdatePrivilege,
  });

  const roles = rolesQuery?.data as Role[];
  const permissions = permissionsQuery?.data?.data as Permission[];

  const roleOptions = roles?.map((role) => (
    <option key={role.id} value={role.id}>
      {role.name}
    </option>
  ));

  const permissionOptions = permissions?.map((permission) => ({
    label: permission.name,
    value: permission.id,
  }));

  const updatePrivilegeHandler = async (values: FormikValues) => {
    const selectedPermissionId = values.permissionIds?.[0];
    const payload: UpdatePrivilegePayload = {
      id: privilege.id,
      roleId: values.roleId,
      permissionId: Number(selectedPermissionId),
    };

    try {
      await updatePrivilegeMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message || 'Privilege updated successfully',
          });

          queryClient.refetchQueries({ queryKey: ['get-all-privileges'] });
          refetch();

          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to update privilege',
      });
    }
  };

  return (
    <Formik
      initialValues={{
        roleId: privilege.roleId,
        permissionIds: [privilege.permissionId],
      }}
      onSubmit={(values) => {
        updatePrivilegeHandler(values);
      }}
      validationSchema={validate}
      enableReinitialize={true}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form className="fields">
            <CustomSelect
              name="roleId"
              placeholder="Select Role"
              label="Role"
              options={roleOptions}
              loading={rolesQuery.isLoading}
            />

            <>
              <h5>Permissions</h5>
              <AntSelect
                mode="multiple"
                placeholder="Select Permissions"
                options={permissionOptions}
                value={values.permissionIds}
                onChange={(value) => setFieldValue('permissionIds', value)}
                loading={permissionsQuery.isLoading}
                size="large"
              />
            </>

            <div className="btn-group">
              <Button onClick={handleClose} variant="text" text="Cancel" />
              <Button
                text={updatePrivilegeMutation.isPending ? 'Updating...' : 'Update'}
                type="submit"
                isLoading={updatePrivilegeMutation?.isPending}
                disabled={updatePrivilegeMutation?.isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditPrivilege;
